import classNames from 'classnames';
import { memo, useEffect, useState } from 'react';
import styled from 'styled-components';
import { MfeReactComponent } from '@carvana/microfrontend-react';
import { hasMobileUserAgent } from '@carvana/stc-utilities';

import type { MfeChildProps } from '../../mfeTypes';

import ErrorBoundary from './ErrorBoundary';
import QualiSurvey from './QualiSurvey';

const DisplayController = styled.div`
  &.transposeContent {
    display: none;
  }

  @media print {
    display: none;
  }
`;

const WizardContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  margin: 0 auto;
  &.nativeMobile {
    height: max-content;
  }
`;

const Robe = styled.main`
  background: ${({ theme }) => theme.carvana.white.primary};
  justify-self: center;
  margin-left: auto;
  margin-right: auto;
  max-width: 1800px;
  width: 100%;

  @media print {
    background-color: white;
  }
`;

const chatContext = {
  contextType: 'carvana_trade'
};

const DummyHeader = styled.div`
  background-color: var(--canvas-default);
  height: 129px;
  max-width: 1800px;
  margin: 0 auto;

  @media (min-width: 768px) {
    height: 79px;
  }
`;

const Header = memo(() => (
  <MfeReactComponent component="default" module="HeaderModule" loading={<DummyHeader />}>
    {({ Component: HeaderComponent }: MfeChildProps) => (
      <HeaderComponent
        environment={process.env.CARVANA_APP_ENVIRONMENT}
        chatContext={chatContext}
        useProvider
      />
    )}
  </MfeReactComponent>
));

const Footer = memo(() => (
  <MfeReactComponent component="default" module="FooterModule">
    {({ Component: FooterComponent }: MfeChildProps) => (
      <FooterComponent environment={process.env.CARVANA_APP_ENVIRONMENT} withoutProvider />
    )}
  </MfeReactComponent>
));

type BasePageProps = {
  children: React.ReactNode;
  transpose?: boolean;
};

const BasePage = ({ children, transpose = false }: BasePageProps) => {
  const [transposeContent, setTranspose] = useState(transpose);

  useEffect(() => {
    if (transpose !== transposeContent) {
      setTranspose(hasMobileUserAgent() || transpose);
    }
  }, [setTranspose, transpose, transposeContent]);

  return (
    <WizardContainer
      id="wizard-container"
      className={classNames({ nativeMobile: hasMobileUserAgent(window.navigator) })}
    >
      <QualiSurvey />
      <DisplayController className={classNames({ transposeContent })}>
        <Header />
      </DisplayController>
      <ErrorBoundary>
        <Robe id="sell-to-carvana">{children}</Robe>
      </ErrorBoundary>
      <DisplayController className={classNames({ transposeContent })}>
        <Footer />
      </DisplayController>
    </WizardContainer>
  );
};

export default BasePage;
